import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Loader2, AlertTriangle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { STATUS_META } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const empty = { name: "", phone: "", email: "", company: "", status: "new", follow_up_date: null, job_date: null, notes: "" };

export default function CustomerDialog({ open, onOpenChange, customer, onSaved }) {
  const editing = !!customer;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(customer ? { ...empty, ...customer } : empty);
      setDupes([]);
    }
  }, [open, customer]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDupes = async () => {
    if (editing) return;
    const phone = (form.phone || "").trim();
    const email = (form.email || "").trim();
    if (!phone && !email) { setDupes([]); return; }
    try {
      const { data } = await client.get("/duplicate-check", { params: { phone: phone || undefined, email: email || undefined } });
      setDupes(data.matches || []);
    } catch { /* non-blocking */ }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        company: form.company || null,
        status: form.status,
        follow_up_date: form.follow_up_date || null,
        job_date: form.job_date || null,
        notes: form.notes || null,
      };
      let data;
      if (editing) {
        ({ data } = await client.put(`/customers/${customer.id}`, payload));
      } else {
        ({ data } = await client.post("/customers", payload));
      }
      toast.success(editing ? "Customer updated" : "Customer added");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{editing ? "Edit customer" : "New walk-in"}</DialogTitle>
          <DialogDescription>Capture the lead's contact details, pipeline stage and next follow-up.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>Full name *</Label>
            <Input data-testid="cust-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. Sarah Nguyen" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Phone</Label>
              <Input data-testid="cust-phone-input" value={form.phone || ""} onChange={(e) => set("phone")(e.target.value)} onBlur={checkDupes} placeholder="04xx xxx xxx" />
            </div>
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input data-testid="cust-email-input" value={form.email || ""} onChange={(e) => set("email")(e.target.value)} onBlur={checkDupes} placeholder="name@email.com" />
            </div>
          </div>
          {!editing && dupes.length > 0 && (
            <div className="rounded-lg border border-warning/40 bg-[#D97706]/10 p-3" data-testid="duplicate-warning">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-warning"><AlertTriangle className="h-4 w-4" /> Possible duplicate{dupes.length > 1 ? "s" : ""}</div>
              <div className="mt-2 space-y-1">
                {dupes.map((d) => (
                  <Link key={d.id} to={`/customers/${d.id}`} onClick={() => onOpenChange(false)} data-testid={`dupe-${d.id}`} className="block truncate text-sm text-ink underline-offset-2 hover:underline">
                    {d.name} · {d.phone || d.email}
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Company / project</Label>
            <Input data-testid="cust-company-input" value={form.company || ""} onChange={(e) => set("company")(e.target.value)} placeholder="Optional" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Stage</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="cust-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Next follow-up</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="cust-followup-trigger" className="justify-start font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.follow_up_date ? format(parseISO(form.follow_up_date), "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.follow_up_date ? parseISO(form.follow_up_date) : undefined}
                    onSelect={(d) => set("follow_up_date")(d ? format(d, "yyyy-MM-dd") : null)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {form.status === "booked" && (
            <div className="grid gap-1.5">
              <Label>Job date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="cust-jobdate-trigger" className="justify-start font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.job_date ? format(parseISO(form.job_date), "d MMM yyyy") : "Pick job date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={form.job_date ? parseISO(form.job_date) : undefined} onSelect={(d) => set("job_date")(d ? format(d, "yyyy-MM-dd") : null)} initialFocus />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Booked jobs appear on the Calendar.</p>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="cust-notes-input" value={form.notes || ""} onChange={(e) => set("notes")(e.target.value)} placeholder="What are they looking for?" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cust-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cust-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save changes" : "Add customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
